import { StoreState } from '@/store';
import { getCurrentUserData } from '@/store/login/selectors';
import { CacheService } from '@/utils/cacheService/cache.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FilterList, Person, UserDataList, UserDataRow } from '../../interfaces/person';
import { ParseRutPipe } from '../../pipes/parse-rut.pipe';

@Injectable({
  providedIn: 'root',
})
export class PersonService {

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private store: Store<StoreState>,
  ) {

  }

  getFilteredList(filterText: string, includeMyself: boolean = false): Observable<FilterList> {

    if (!filterText) {
      return of([]);
    }

    const personList$ = this.http.get<UserDataList>(`User/UsersSearch/${filterText}`);

    const storeLoginSlice$ = getCurrentUserData(this.store);

    return combineLatest(personList$, storeLoginSlice$)
      .pipe(
        map(
          ([array, person]) => this.getParsedList(array, person, includeMyself),
        ),
      );
  }
  getFilteredListExeptEmployee(filterText: string): Observable<FilterList> {

    if (!filterText) {
      return of([]);
    }

    const personList$ = this.http.get<UserDataList>(`User/UsersSearch/DifferentToEmployeer/${filterText}`);

    const storeLoginSlice$ = getCurrentUserData(this.store);

    return combineLatest(personList$, storeLoginSlice$)
      .pipe(
        map(
          ([array, person]) => this.getParsedList(array, person),
        ),
      );
  }
  getFilteredListExeptContractor(filterText: string): Observable<FilterList> {
    if (!filterText) {
      return of([]);
    }

    const personList$ = this.http.get<UserDataList>(`/User/UsersSearch/DifferentToContractor/${filterText}`);

    const storeLoginSlice$ = getCurrentUserData(this.store);

    return combineLatest(personList$, storeLoginSlice$)
      .pipe(
        map(
          ([array, person]) => this.getParsedList(array, person),
        ),
      );
  }

  getFilteredListRole(filterText: string, roleId: number, includeMyself: boolean = false, fullAccess: boolean = false): Observable<FilterList> {
    if (!filterText) {
      return of([]);
    }

    const personList$ = this.http.get<UserDataList>(`User/UsersSearch/${filterText}/Role/${roleId}`);
    const storeLoginSlice$ = getCurrentUserData(this.store);

    return combineLatest(personList$, storeLoginSlice$)
      .pipe(
        map(
          ([array, person]) => {
            const self: UserDataRow[] = [];
            if (fullAccess && includeMyself) {
              const alreadyOnArr = array.find((x)=> x.id === person.id);
              if (!alreadyOnArr) {
                self.push({
                  id: person.id,
                  fullName: person.fullName,
                  mdmid: person.mdmid,
                  passport: person.passport,
                  email: person.email,
                  rutDv: person.rutDv,
                });
              }
            }
            return this.getParsedList([...array, ...self], person, includeMyself);
          },
        ),
      );
  }

  getPersonDataByRutDv(filterText: string): Observable<Person> {
    return this.http.get<UserDataList>(`User/UsersSearch/${filterText}`)
      .pipe(
        map<UserDataList, { id: number }>(
          (array) => array.map((userData) => ({
            id: userData.id,
          }))[0],
        ),
        switchMap(({ id }) => this.getPersonDataById(id)),
      );
  }

  // Person data should not change so constantly
  getPersonDataById(id: string | number): Observable<Person> {
    const source$ = this.http.get<Person>(`User/UsersSearchById/${id}`);
    return this.cacheService.save<Person>(`UsersSearchById${id}`, 24 * 60, source$);
  }

  // Person data should not change so constantly
  getPersonDataByMdmid(mdmid: string): Observable<Person> {
    const source$ = this.http.get<[Person]>(`/User/UserInfo/${mdmid}`).pipe(map((array) => array[0]));
    return this.cacheService.save<Person>(`UserInfo${mdmid}`, 24 * 60, source$);
  }

  getParsedList(array: UserDataList, person: Person, includeMyself: boolean = false): FilterList {
    const parseRutPipe = new ParseRutPipe();
    const rutOrPassport = (rutDv: string, passport: string) => (rutDv ? parseRutPipe.transform(rutDv) : passport);
    return array.map((userData) => ({
      id: userData.id.toString(),
      value: `${userData.fullName} - ${!userData.email ? 'Sin correo' : userData.email} - ${rutOrPassport(userData.rutDv, userData.passport)}`,
      mdmid: userData.mdmid,
      email: userData.email,
    }))
      .filter(({ id }) => (includeMyself ? true : +id !== +person.id));
  }

}
